import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import usePageTracking from 'utils/hooks/usePageTracking';
import {
  runKeywordDiscoveryAnalysis,
  getSpecificKeywordResult,
  normalizeKeywordRows,
  normalizeProductionLabel,
  extractStatusFromResult,
  KEYWORD_STATUS,
  getWebsiteLanguage,
  getWebsiteCountry,
  mapCountryToApiEnum,
  mapLanguageToApiEnum,
  saveKeywordExplorerResults,
  toCsv,
  downloadFile,
  diffStyle,
  fmt,
} from './keywordDiscoveryApi';
import CreateIAbrief from 'pages/AdminClient/RedactionIA/createIAbrief';
import SmartContentPopin from 'components/shared/SmartcontentModal/SmartContentPopin';
import AssignTextToProjectPopin from 'components/shared/SmartcontentModal/AssignTextToProjectPopin';
import RedactionModal from 'components/shared/Redactionmodal/RedactionModal';
import ProjectCreatedModal from 'components/shared/modal/ProjectCreatedModal';
import BuyCreditsModal from 'components/shared/BuyCreditsModal';
import axiosPrivate from 'config/axiosPrivate';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';
import { adminClientClientListSelector } from 'store/AdminClient/ClientList';
import { fetchAdminClientClientList } from 'store/AdminClient/ClientList/features';
import { setAISmartContentType } from 'store/AdminClient/AISmartContentType';
import { UserSessionSelector } from 'store/UserSession';
import { formatWebsiteThematicsService } from 'utils/websiteThematics';
import { getProjectId } from 'utils/smartContent/useAssignTextToProject';
import { usePagination } from 'utils/hooks/usePagination';
import './GenerateKeyword.css';

const ADMIN_CLIENT_GENERATE_KEYWORD_PAGE = '/AdminClient/GenerateKeyword';
const SMART_POPIN_CONTEXT_KEY = 'SmartContentPopinContext';
const DEFAULT_CONTENT_TYPE = 'OTHER';
const PAGE_SIZE_OPTIONS = [10, 25, 50];

const isHumanProduction = (production) => {
  const value = String(production || '').trim().toLowerCase();
  return value === 'humain' || value === 'human' || value.includes('humain');
};

const isPremiumProject = (project) =>
  String(project?.type || project?.projectType || '').trim().toUpperCase() === 'PREMIUM';

const STEPS = [
  { n: 1, label: 'Recherche de mots-clés' },
  { n: 2, label: 'Générateur de brief' },
  { n: 3, label: 'Rédaction IA' },
];

const normalizeUrl = (input) =>
  (input || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '');

const normalizeLlmId = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'both' || normalized === 'claude+gpt' || normalized === 'gpt+claude') {
    return 'claude_gpt';
  }
  return normalized.replace(/\s+/g, '_');
};

const formatModelName = (id) => {
  if (id === 'gpt') return 'GPT';
  if (id === 'claude') return 'Claude';
  if (id === 'claude_gpt') return 'Claude + GPT';
  if (id === 'perplexity') return 'Perplexity';
  if (id === 'gemini') return 'Gemini';
  return String(id || '').toUpperCase();
};

const Icon = {
  arrowLeft: (p) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  bolt: (p) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" {...p}>
      <path d="M13 2L3 14h7l-1 8 10-12h-7z" />
    </svg>
  ),
  save: (p) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <path d="M17 21v-8H7v8M7 3v5h8" />
    </svg>
  ),
  doc: (p) => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="var(--gk-ink2)" strokeWidth="1.8" {...p}>
      <path d="M14 3v4a1 1 0 0 0 1 1h4" />
      <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
    </svg>
  ),
  download: (p) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
    </svg>
  ),
  check: (p) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  mail: (p) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--gk-accent-600)" strokeWidth="1.9" {...p}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  ),
  filter: (p) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M4 5h16l-6 7v5l-4 2v-7z" />
    </svg>
  ),
};

function Banner({ icon, title, text }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        background: 'var(--gk-accent-050)',
        borderLeft: '4px solid var(--gk-accent)',
        borderRadius: 10,
        padding: '16px 20px',
        marginBottom: 22,
      }}
    >
      <span style={{ fontSize: 17 }}>{icon}</span>
      <div>
        <div style={{ color: 'var(--gk-accent-600)', fontWeight: 600, fontSize: 15 }}>{title}</div>
        <div style={{ color: 'var(--gk-ink2)', fontSize: 14, marginTop: 2 }}>{text}</div>
      </div>
    </div>
  );
}

function listBounds(items, key) {
  const values = items
    .map((item) => Number(item?.[key]))
    .filter((value) => Number.isFinite(value));
  if (!values.length) return { min: 0, max: 0 };
  return {
    min: Math.min(...values),
    max: Math.max(...values),
  };
}

function parseFilterBound(value, fallback) {
  if (value === '' || value == null) return fallback;
  const normalized = String(value).replace(/[\s\u202f\u00a0]/g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatInputNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n
    .toLocaleString('fr-FR', { maximumFractionDigits: 2 })
    .replace(/[\u202f\u00a0]/g, ' ');
}

function formatCompactVolume(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const abs = Math.abs(n);
  const compact = (scaled) =>
    (Math.round(scaled * 10) / 10)
      .toLocaleString('fr-FR', { maximumFractionDigits: 1 })
      .replace(/[\u202f\u00a0]/g, ' ');
  if (abs >= 1000000) return `${compact(n / 1000000)} M`;
  if (abs >= 1000) return `${compact(n / 1000)} k`;
  return formatInputNumber(n);
}

function adjustBound(value, direction, step) {
  const current = parseFilterBound(value, 0);
  const next = Math.max(0, current + direction * step);
  return formatInputNumber(Math.round(next * 100) / 100);
}

function RangeFilter({ label, hint, minLabel, maxLabel, minValue, maxValue, onMinChange, onMaxChange, onApply, step = 1 }) {
  const { t } = useTranslation();

  const applyOnEnter = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onApply?.();
    }
  };

  return (
    <div className="gk-range-filter">
      <span className="gk-range-filter-label">
        {label}
        {hint ? <em>({hint})</em> : null}
      </span>
      <div className="gk-range-control">
        <div className="gk-range-side">
          <span>{minLabel}</span>
          <input
            type="text"
            inputMode="decimal"
            value={minValue}
            onChange={(e) => onMinChange(e.target.value)}
            onKeyDown={applyOnEnter}
            aria-label={`${label} ${minLabel}`}
          />
          <span className="gk-range-steps">
            <button
              type="button"
              className="gk-range-step"
              onClick={() => onMinChange(adjustBound(minValue, 1, step))}
              aria-label={t('Augmenter')}
            >
              +
            </button>
            <button
              type="button"
              className="gk-range-step"
              onClick={() => onMinChange(adjustBound(minValue, -1, step))}
              aria-label={t('Diminuer')}
            >
              −
            </button>
          </span>
        </div>
        <span className="gk-range-dash" aria-hidden="true" />
        <div className="gk-range-side">
          <span>{maxLabel}</span>
          <input
            type="text"
            inputMode="decimal"
            value={maxValue}
            onChange={(e) => onMaxChange(e.target.value)}
            onKeyDown={applyOnEnter}
            aria-label={`${label} ${maxLabel}`}
          />
          <span className="gk-range-steps">
            <button
              type="button"
              className="gk-range-step"
              onClick={() => onMaxChange(adjustBound(maxValue, 1, step))}
              aria-label={t('Augmenter')}
            >
              +
            </button>
            <button
              type="button"
              className="gk-range-step"
              onClick={() => onMaxChange(adjustBound(maxValue, -1, step))}
              aria-label={t('Diminuer')}
            >
              −
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

function ProductionFilter({ value, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const options = [
    { value: 'all', label: t('Toutes') },
    { value: 'IA', label: t('IA') },
    { value: 'Hybride', label: t('Hybride') },
    { value: 'Humain', label: t('Humain') },
  ];
  const current = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="gk-filters-production" ref={rootRef}>
      <span className="gk-range-filter-label">{t('Production')}</span>
      <div className="gk-filters-select-wrap">
        <button
          type="button"
          className="gk-filters-select"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((isOpen) => !isOpen)}
        >
          {current.label}
        </button>
        <span className={`gk-filters-select-arrow${open ? ' is-open' : ''}`} aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
        {open && (
          <ul className="gk-filters-menu" role="listbox">
            {options.map((option) => (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  className={option.value === value ? 'is-active' : undefined}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const GenerateKeyword = () => {
  usePageTracking(ADMIN_CLIENT_GENERATE_KEYWORD_PAGE);
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const [step, setStep] = useState(1);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [lastQuery, setLastQuery] = useState('');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [volumeMin, setVolumeMin] = useState('');
  const [volumeMax, setVolumeMax] = useState('');
  const [scoreMin, setScoreMin] = useState('');
  const [scoreMax, setScoreMax] = useState('');
  const [filterDiff, setFilterDiff] = useState('all');
  const [appliedFilters, setAppliedFilters] = useState({
    volumeMin: '',
    volumeMax: '',
    scoreMin: '',
    scoreMax: '',
    diff: 'all',
  });
  const [sortKey, setSortKey] = useState('interest');
  const [sortDir, setSortDir] = useState('desc');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selected, setSelected] = useState({});

  const [workKw, setWorkKw] = useState(null);
  const [workKwProduction, setWorkKwProduction] = useState('');

  const [isSmartContentPopinOpen, setIsSmartContentPopinOpen] = useState(false);
  const [isPremiumProjectPopinOpen, setIsPremiumProjectPopinOpen] = useState(false);
  const [premiumProjects, setPremiumProjects] = useState([]);
  const [loadingPremiumProjects, setLoadingPremiumProjects] = useState(false);
  const [premiumProjectError, setPremiumProjectError] = useState('');
  const [isLoadingPremiumOrder, setIsLoadingPremiumOrder] = useState(false);
  const [selectedPremiumOrder, setSelectedPremiumOrder] = useState(null);
  const [isRedactionModalOpen, setIsRedactionModalOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [availableAiModels, setAvailableAiModels] = useState([]);
  const [gapAnalysisEnabled, setGapAnalysisEnabled] = useState(null);
  const [remainingCredits, setRemainingCredits] = useState(null);
  const [isCreditsLimitModalOpen, setIsCreditsLimitModalOpen] = useState(false);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);

  const [selectedWebsiteUrl] = useWebsiteFilter('AC_Dashboard_SelectedWebsite');
  const { clients } = useSelector(adminClientClientListSelector) || {};
  const { user } = useSelector(UserSessionSelector) || {};
  const clientsList = Array.isArray(clients) ? clients : [];
  const hasNoWebsiteConfigured = clients !== null && clientsList.length === 0;
  const pollActiveRef = React.useRef(false);

  const selectedWebsite = useMemo(() => {
    if (!selectedWebsiteUrl || clientsList.length === 0) return null;
    const normalizedSaved = normalizeUrl(selectedWebsiteUrl);
    return (
      clientsList.find((website) => {
        const wUrl = website?.urlSite || website?.url || '';
        const wName = website?.name || '';
        return (
          wUrl === selectedWebsiteUrl ||
          wName === selectedWebsiteUrl ||
          normalizeUrl(wUrl) === normalizedSaved ||
          normalizeUrl(wName) === normalizedSaved
        );
      }) || null
    );
  }, [clientsList, selectedWebsiteUrl]);

  const smartPopinInitialValues = useMemo(
    () => ({
      keyword: workKw ? String(workKw).trim() : '',
    }),
    [workKw]
  );

  useEffect(() => {
    document.title = `Typix - ${t('Générer un mot-clé')}`;
  }, [t]);

  useEffect(() => {
    if (clients === null) {
      dispatch(fetchAdminClientClientList());
    }
  }, [dispatch, clients]);

  useEffect(() => {
    pollActiveRef.current = true;
    return () => {
      pollActiveRef.current = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchCompanyQuotas = async () => {
      try {
        const { data } = await axiosPrivate.get('/project/company-quotas');
        if (!isMounted) return;
        setGapAnalysisEnabled(data?.gapAnalysisEnabled === true);
        const totalCredits = Number(data?.totalCredits);
        const usedCredits = Number(data?.usedCredits);
        if (Number.isFinite(totalCredits) && Number.isFinite(usedCredits)) {
          setRemainingCredits(totalCredits - usedCredits);
        } else {
          const apiRemainingCredits = Number(data?.remainingCredits);
          setRemainingCredits(
            Number.isFinite(apiRemainingCredits) ? apiRemainingCredits : null
          );
        }
        const rawModels = Array.isArray(data?.availableAiModels) ? data.availableAiModels : [];
        const normalizedModels = rawModels
          .map((model) => {
            const id = normalizeLlmId(
              typeof model === 'string' ? model : model?.id || model?.value || model?.name || ''
            );
            if (!id) return null;
            return {
              id,
              name: typeof model === 'object' && model?.name ? model.name : formatModelName(id),
            };
          })
          .filter(Boolean);
        setAvailableAiModels(
          Array.from(new Map(normalizedModels.map((item) => [item.id, item])).values())
        );
      } catch {
        if (isMounted) {
          setGapAnalysisEnabled(false);
          setAvailableAiModels([]);
          setRemainingCredits(null);
        }
      }
    };

    fetchCompanyQuotas();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedWebsiteUrl || !selectedWebsite) {
      setAgents([]);
      return undefined;
    }

    let isMounted = true;
    const websiteParam = encodeURIComponent(selectedWebsiteUrl);

    const fetchAgents = async () => {
      setAgentsLoading(true);
      try {
        const agentsUrl = `/smart-content/agent_ia/redaction/free_content_type?websiteUrl=${websiteParam}`;
        const agentsResp = await axiosPrivate.get(agentsUrl);
        if (!isMounted) return;
        setAgents(Array.isArray(agentsResp?.data) ? agentsResp.data : []);
      } catch {
        if (isMounted) setAgents([]);
      } finally {
        if (isMounted) setAgentsLoading(false);
      }
    };

    fetchAgents();
    return () => {
      isMounted = false;
    };
  }, [selectedWebsiteUrl, selectedWebsite]);

  const hasResults = status === 'done' && results.length > 0;

  const analysisContext = useMemo(() => {
    const websiteUrl =
      selectedWebsite?.urlSite || selectedWebsite?.url || selectedWebsiteUrl || '';
    const account =
      selectedWebsite?.account ||
      user?.companyName ||
      user?.account ||
      '';
    const executorPersonEmail = user?.mail || user?.email || '';
    return {
      account: String(account || '').trim(),
      websiteUrl: String(websiteUrl || '').trim(),
      executorPersonEmail: String(executorPersonEmail || '').trim(),
    };
  }, [selectedWebsite, selectedWebsiteUrl, user]);

  const volumeBounds = useMemo(() => listBounds(results, 'volume'), [results]);
  const scoreBounds = useMemo(() => listBounds(results, 'interest'), [results]);

  useEffect(() => {
    if (!results.length) return;
    const next = {
      volumeMin: formatInputNumber(volumeBounds.min),
      volumeMax: formatInputNumber(volumeBounds.max),
      scoreMin: formatInputNumber(scoreBounds.min),
      scoreMax: formatInputNumber(scoreBounds.max),
      diff: 'all',
    };
    setVolumeMin(next.volumeMin);
    setVolumeMax(next.volumeMax);
    setScoreMin(next.scoreMin);
    setScoreMax(next.scoreMax);
    setFilterDiff('all');
    setAppliedFilters(next);
  }, [results, volumeBounds.min, volumeBounds.max, scoreBounds.min, scoreBounds.max]);

  const filtered = useMemo(() => {
    const activeVolumeMin = parseFilterBound(appliedFilters.volumeMin, volumeBounds.min);
    const activeVolumeMax = parseFilterBound(appliedFilters.volumeMax, volumeBounds.max);
    const activeScoreMin = parseFilterBound(appliedFilters.scoreMin, scoreBounds.min);
    const activeScoreMax = parseFilterBound(appliedFilters.scoreMax, scoreBounds.max);
    const r = results.filter(
      (k) =>
        k.volume >= activeVolumeMin &&
        k.volume <= activeVolumeMax &&
        k.interest >= activeScoreMin &&
        k.interest <= activeScoreMax &&
        (appliedFilters.diff === 'all' ||
          normalizeProductionLabel(k.difficulty) === appliedFilters.diff)
    );
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...r].sort((a, b) => (a[sortKey] - b[sortKey]) * dir);
  }, [results, appliedFilters, volumeBounds, scoreBounds, sortKey, sortDir]);

  const applyFilters = () => {
    setAppliedFilters({
      volumeMin,
      volumeMax,
      scoreMin,
      scoreMax,
      diff: filterDiff,
    });
  };

  const resetFilters = () => {
    const next = {
      volumeMin: formatInputNumber(volumeBounds.min),
      volumeMax: formatInputNumber(volumeBounds.max),
      scoreMin: formatInputNumber(scoreBounds.min),
      scoreMax: formatInputNumber(scoreBounds.max),
      diff: 'all',
    };
    setVolumeMin(next.volumeMin);
    setVolumeMax(next.volumeMax);
    setScoreMin(next.scoreMin);
    setScoreMax(next.scoreMax);
    setFilterDiff('all');
    setAppliedFilters(next);
  };

  const clearVolumeFilter = () => {
    const min = formatInputNumber(volumeBounds.min);
    const max = formatInputNumber(volumeBounds.max);
    setVolumeMin(min);
    setVolumeMax(max);
    setAppliedFilters((prev) => ({ ...prev, volumeMin: min, volumeMax: max }));
  };

  const clearScoreFilter = () => {
    const min = formatInputNumber(scoreBounds.min);
    const max = formatInputNumber(scoreBounds.max);
    setScoreMin(min);
    setScoreMax(max);
    setAppliedFilters((prev) => ({ ...prev, scoreMin: min, scoreMax: max }));
  };

  const clearProductionFilter = () => {
    setFilterDiff('all');
    setAppliedFilters((prev) => ({ ...prev, diff: 'all' }));
  };

  const { currentPage, setCurrentPage, totalPages, paginatedItems } = usePagination(
    filtered,
    itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [
    appliedFilters,
    sortKey,
    sortDir,
    results,
    itemsPerPage,
    setCurrentPage,
  ]);

  async function runSearch() {
    const q = query.trim();
    if (!q || status === 'loading') return;

    const { account, websiteUrl, executorPersonEmail } = analysisContext;

    // Always resolve country/language from website details (not a hardcoded FR default)
    let websiteDetails = selectedWebsite;
    const websiteId = selectedWebsite?.id || selectedWebsite?.websiteId;
    if (websiteId) {
      try {
        const { data } = await axiosPrivate.get(`/project/manager/website/${websiteId}`);
        if (data) websiteDetails = { ...selectedWebsite, ...data };
      } catch {
        // keep list payload
      }
    }

    const country = mapCountryToApiEnum(getWebsiteCountry(websiteDetails));
    const language = mapLanguageToApiEnum(getWebsiteLanguage(websiteDetails));

    if (!selectedWebsiteUrl || !websiteUrl) {
      setStatus('error');
      setError(t('Veuillez sélectionner un site web avant de lancer l’analyse.'));
      return;
    }
    if (!account) {
      setStatus('error');
      setError(t('Impossible de déterminer le compte (company name).'));
      return;
    }
    if (!executorPersonEmail) {
      setStatus('error');
      setError(t('Impossible de déterminer l’e-mail de l’exécuteur.'));
      return;
    }
    if (!language) {
      setStatus('error');
      setError(t('La langue du site web est introuvable. Vérifiez la configuration du site.'));
      return;
    }
    if (!country) {
      setStatus('error');
      setError(t('Le pays du site web est introuvable. Vérifiez la configuration du site.'));
      return;
    }

    setStatus('loading');
    setError('');
    setResults([]);
    setSelected({});
    setWorkKw(null);
    setWorkKwProduction('');
    setSaved(false);
    setSaveError('');
    pollActiveRef.current = true;

    try {
      const outcome = await runKeywordDiscoveryAnalysis({
        account,
        websiteUrl,
        country,
        language,
        keyword: q,
        executorPersonEmail,
        shouldContinue: () => pollActiveRef.current,
      });

      if (outcome.cancelled) return;

      if (outcome.status === KEYWORD_STATUS.FAILED) {
        setStatus('error');
        setError(t('Erreur – à relancer'));
        return;
      }

      let rows = outcome.rows;
      if (!rows.length && outcome.status === KEYWORD_STATUS.DONE) {
        const payload = await getSpecificKeywordResult({
          account,
          websiteUrl,
          keyword: q,
        });
        rows = normalizeKeywordRows(payload);
      }

      if (!rows.length) throw new Error('empty');
      setResults(rows);
      setLastQuery(q);
      setStatus('done');
      setFilterDiff('all');
      setSortKey('interest');
      setSortDir('desc');
    } catch {
      setStatus('error');
      setError(t('La génération a échoué. Réessayez.'));
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(history.location.search || '');
    const keywordFromUrl = String(params.get('keyword') || '').trim();
    if (!keywordFromUrl || !analysisContext.account || !analysisContext.websiteUrl) {
      return undefined;
    }

    let cancelled = false;
    setQuery(keywordFromUrl);
    setStatus('loading');
    setError('');
    setSaved(false);
    setSaveError('');
    pollActiveRef.current = true;

    (async () => {
      try {
        let payload = await getSpecificKeywordResult({
          account: analysisContext.account,
          websiteUrl: analysisContext.websiteUrl,
          keyword: keywordFromUrl,
        });
        let apiStatus = extractStatusFromResult(payload) || KEYWORD_STATUS.IN_PROCESS;

        while (apiStatus === KEYWORD_STATUS.IN_PROCESS && pollActiveRef.current && !cancelled) {
          await new Promise((r) => setTimeout(r, 5000));
          payload = await getSpecificKeywordResult({
            account: analysisContext.account,
            websiteUrl: analysisContext.websiteUrl,
            keyword: keywordFromUrl,
          });
          apiStatus = extractStatusFromResult(payload) || KEYWORD_STATUS.IN_PROCESS;
        }

        if (cancelled || !pollActiveRef.current) return;

        if (apiStatus === KEYWORD_STATUS.FAILED) {
          setStatus('error');
          setError(t('Erreur – à relancer'));
          return;
        }

        const rows = normalizeKeywordRows(payload);
        if (!rows.length) throw new Error('empty');
        setResults(rows);
        setLastQuery(keywordFromUrl);
        setStatus('done');
        setFilterDiff('all');
        setSortKey('interest');
        setSortDir('desc');
      } catch {
        if (!cancelled) {
          setStatus('error');
          setError(t('La génération a échoué. Réessayez.'));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [analysisContext.account, analysisContext.websiteUrl, history.location.search]);

  async function handleSaveResults() {
    if (!hasResults || saving || saved) return;

    const anySel = selectionCount > 0;
    const rowsToSave = (anySel ? filtered.filter((r) => selected[r.keyword]) : results).filter(
      Boolean
    );
    if (!rowsToSave.length) {
      setSaveError(t('Sélectionnez au moins un mot-clé à sauvegarder.'));
      return;
    }

    setSaving(true);
    setSaveError('');
    try {
      await saveKeywordExplorerResults(lastQuery || query, rowsToSave);
      setSaved(true);
    } catch {
      setSaveError(t('La sauvegarde a échoué. Réessayez.'));
    } finally {
      setSaving(false);
    }
  }

  function primaryAction() {
    if (hasResults) handleSaveResults();
    else runSearch();
  }

  function setSort(k) {
    if (sortKey === k) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSortKey(k);
      setSortDir('desc');
    }
  }

  const arrow = (k) => (sortKey === k ? (sortDir === 'asc' ? '↑' : '↓') : '');

  const visKeys = filtered.map((r) => r.keyword);
  const allSelected = visKeys.length > 0 && visKeys.every((k) => selected[k]);
  const selectionCount = Object.values(selected).filter(Boolean).length;

  function exportCsv() {
    const anySel = selectionCount > 0;
    const rows = filtered.filter((r) => !anySel || selected[r.keyword]);
    downloadFile(
      `mots-cles-${(lastQuery || 'export').replace(/\s+/g, '-')}.csv`,
      toCsv(rows),
      'text/csv;charset=utf-8'
    );
  }

  const openContentPopin = async () => {
    if (!workKw) return;

    if (isHumanProduction(workKwProduction)) {
      const websiteId = selectedWebsite?.id || selectedWebsite?.websiteId;
      if (!websiteId) {
        setError(t('Veuillez sélectionner un site web avant de continuer.'));
        setStatus('error');
        return;
      }

      setPremiumProjectError('');
      setIsPremiumProjectPopinOpen(true);
      setLoadingPremiumProjects(true);

      try {
        const { data } = await axiosPrivate.get(`/project/by-website/${websiteId}/summary`);
        const list = (Array.isArray(data) ? data : []).filter(isPremiumProject);
        setPremiumProjects(list);
      } catch {
        setPremiumProjects([]);
        setPremiumProjectError('assign_project_load_error');
      } finally {
        setLoadingPremiumProjects(false);
      }
      return;
    }

    setIsSmartContentPopinOpen(true);
  };

  const handlePremiumProjectValidate = async (project) => {
    const projectId = getProjectId(project);
    if (!projectId) {
      setPremiumProjectError('assign_project_selection_error');
      return false;
    }

    setIsLoadingPremiumOrder(true);
    setPremiumProjectError('');

    try {
      const { data: order } = await axiosPrivate.get(`/project/${projectId}`);
      setSelectedPremiumOrder(order);
      setIsPremiumProjectPopinOpen(false);
      setIsRedactionModalOpen(true);
      return true;
    } catch {
      setPremiumProjectError('assign_project_save_error');
      return false;
    } finally {
      setIsLoadingPremiumOrder(false);
    }
  };

  const handleCloseRedactionModal = () => {
    setIsRedactionModalOpen(false);
    setSelectedPremiumOrder(null);
  };

  const handleSelectKeyword = (row) => {
    if (workKw === row.keyword) {
      setWorkKw(null);
      setWorkKwProduction('');
      return;
    }
    setWorkKw(row.keyword);
    setWorkKwProduction(row.difficulty || '');
  };

  const handleSmartContentPopinSubmit = (formValues) => {
    if (hasNoWebsiteConfigured) {
      setIsSmartContentPopinOpen(false);
      history.push('/AdminClient/AjouterSiteWeb');
      return;
    }
    if (remainingCredits !== null && remainingCredits <= 0) {
      setIsSmartContentPopinOpen(false);
      setIsCreditsLimitModalOpen(true);
      return;
    }

    const selectedContentType = DEFAULT_CONTENT_TYPE;

    try {
      const websiteThematicsService = formatWebsiteThematicsService(selectedWebsite);
      const context = {
        type: selectedContentType,
        form: {
          ...formValues,
          website: formValues.website || selectedWebsiteUrl,
          websiteThematicsService,
        },
        meta: {
          websiteId: selectedWebsite?.id,
          websiteUrl: selectedWebsiteUrl,
          websiteThematicsService,
          apiContentType: selectedContentType,
          agentType: selectedContentType,
        },
      };
      window.localStorage.setItem(SMART_POPIN_CONTEXT_KEY, JSON.stringify(context));
    } catch {
      // ignore storage errors
    }

    dispatch(setAISmartContentType(selectedContentType));
    setIsSmartContentPopinOpen(false);
    history.push('/AdminClient/createIAsmarttext');
  };

  const canGoStep = (n) => n === 1 || !!workKw;
  const goStep = (n) => {
    if (canGoStep(n)) setStep(n);
  };

  const handleBack = () => {
    if (step <= 1) {
      history.push('/AdminClient/KeywordDiscovery');
      return;
    }
    setStep((s) => Math.max(1, s - 1));
  };

  const goToCreateBrief = () => {
    if (!workKw) return;
    setStep(2);
  };

  return (
    <div className="generate-keyword-page" style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto', width: '100%', padding: '8px 8px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
          <button
            type="button"
            className="tx-btn tx-ghost"
            style={{ width: 44, padding: 0, justifyContent: 'center' }}
            onClick={handleBack}
            title={t('Étape précédente')}
          >
            <Icon.arrowLeft />
          </button>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            {STEPS.map((d, i) => {
              const active = step === d.n;
              const done = step > d.n;
              const canGo = canGoStep(d.n);
              const circle = done
                ? { background: 'var(--gk-grad)', color: '#fff', border: '2px solid transparent' }
                : active
                  ? { background: '#fff', color: 'var(--gk-accent-600)', border: '2px solid var(--gk-accent)' }
                  : { background: '#fff', color: '#b8b6c6', border: '2px solid #e2e1ea' };
              return (
                <div
                  key={d.n}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: i < STEPS.length - 1 ? 1 : '0 0 auto',
                    minWidth: 0,
                  }}
                >
                  <div
                    onClick={() => goStep(d.n)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 9,
                      cursor: canGo ? 'pointer' : 'default',
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        fontWeight: 600,
                        fontSize: 15,
                        ...circle,
                      }}
                    >
                      {done ? '✓' : d.n}
                    </div>
                    <div
                      style={{
                        fontSize: 13.5,
                        fontWeight: active ? 600 : 500,
                        color: active ? 'var(--gk-accent-600)' : done ? 'var(--gk-ink2)' : '#a9a7b8',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {d.label}
                    </div>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      style={{
                        flex: 1,
                        height: 2,
                        margin: '0 4px 26px',
                        background: step > d.n ? 'var(--gk-accent-100)' : '#e6e5ee',
                        minWidth: 20,
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <main style={{ flex: 1, width: '100%', maxWidth: 1240, margin: '0 auto', padding: '26px 8px 80px' }}>
        {step === 1 && (
          <div className="gk-anim-in">
            <Banner
              icon="🔑"
              title={t('Étape 1 — Recherche de mots-clés')}
              text={t('Saisissez un mot-clé ou une thématique. Nous générons automatiquement les mots-clés les plus pertinents à travailler, classés selon leur potentiel SEO et leur volume de recherche.')}
            />

            <div
              style={{
                background: 'var(--gk-card)',
                border: '1px solid var(--gk-border)',
                borderRadius: 14,
                padding: 26,
                boxShadow: '0 1px 3px rgba(20,18,50,.04)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <Icon.doc />
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{t('Informations générales')}</h2>
                <button
                  type="button"
                  className="tx-btn tx-primary"
                  style={{ marginLeft: 'auto' }}
                  onClick={primaryAction}
                  disabled={
                    !query.trim() ||
                    !selectedWebsiteUrl ||
                    status === 'loading' ||
                    saving ||
                    (hasResults && saved)
                  }
                >
                  {status === 'loading' ? (
                    <>
                      <span className="spin" /> {t('Analyse…')}
                    </>
                  ) : saving ? (
                    <>
                      <span className="spin" /> {t('Sauvegarde…')}
                    </>
                  ) : hasResults ? (
                    <>
                      <Icon.save /> {saved ? t('Sauvegardé ✓') : t('Sauvegarder')}
                    </>
                  ) : (
                    <>
                      <Icon.bolt /> {t('Générer')}
                    </>
                  )}
                </button>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                  <label
                    htmlFor="gk-keyword"
                    style={{ display: 'block', fontWeight: 600, fontSize: 13.5, marginBottom: 7 }}
                  >
                    {t('Mot clé ou Thématique')}<span style={{ color: 'var(--gk-red)' }}>*</span>
                  </label>
                  <input
                    id="gk-keyword"
                    className="tx-input"
                    placeholder={t('ex. assurance emprunteur')}
                    value={query}
                    readOnly={hasResults}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') runSearch();
                    }}
                    style={
                      hasResults
                        ? { background: '#faf9fc', color: 'var(--gk-ink2)', cursor: 'not-allowed' }
                        : undefined
                    }
                  />
                </div>
              </div>
              {hasResults && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, color: '#1f9254', fontSize: 13 }}>
                  <Icon.check /> {t('Analyse terminée — un e-mail de notification vous a été envoyé. Choisissez ci-dessous le mot-clé le plus pertinent.')}

                </div>
              )}
              {saveError && (
                <div style={{ color: 'var(--gk-red)', fontSize: 13.5, marginTop: 14 }}>
                  {saveError}
                </div>
              )}
              {status === 'error' && (
                <div style={{ color: 'var(--gk-red)', fontSize: 13.5, marginTop: 14 }}>
                  {error}{' '}
                  <a
                    href="#retry"
                    onClick={(e) => {
                      e.preventDefault();
                      runSearch();
                    }}
                  >
                    {t('Réessayer')}
                  </a>
                </div>
              )}
            </div>

            {status === 'loading' && (
              <div
                style={{
                  marginTop: 22,
                  background: '#fff',
                  border: '1px solid var(--gk-border)',
                  borderRadius: 14,
                  padding: 26,
                  boxShadow: '0 1px 3px rgba(20,18,50,.04)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
                  <span
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'var(--gk-accent-050)',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <span className="spin" style={{ color: 'var(--gk-accent)', width: 18, height: 18 }} />
                  </span>
                  <div>
                    <div style={{ fontSize: 15.5, fontWeight: 600 }}>{t('Analyse en cours…')}</div>
                    <div style={{ fontSize: 13.5, color: 'var(--gk-ink2)', marginTop: 2 }}>
                      {t('Cette analyse peut prendre plusieurs minutes selon la thématique.')}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    background: 'var(--gk-accent-050)',
                    borderRadius: 10,
                    padding: '12px 14px',
                    marginTop: 14,
                  }}
                >
                  <Icon.mail style={{ flexShrink: 0, marginTop: 1 }} />
                  <div style={{ fontSize: 13, color: 'var(--gk-ink2)', lineHeight: 1.5 }}>
                    {t('Vous pouvez fermer cette page :')}{' '}
                    <strong style={{ color: 'var(--gk-accent-600)' }}>{t('un e-mail vous sera envoyé')}</strong> {t('dès que les résultats seront prêts. Vous pourrez alors consulter les mots-clés et choisir le plus pertinent.')}
                  </div>
                </div>
                <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {[0, 0.15, 0.3].map((d, i) => (
                    <div key={i} className="gk-skeleton" style={{ animationDelay: `${d}s` }} />
                  ))}
                </div>
              </div>
            )}

            {hasResults && (
              <div className="gk-anim-in-slow" style={{ marginTop: 26 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
                  <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>
                    {t('{{count}} idées pour « {{query}} »', { count: results.length, query: lastQuery })}
                  </h3>
                  <span style={{ color: 'var(--gk-muted)', fontSize: 13 }}>{filtered.length} {t('affichées')}</span>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                    {selectionCount > 0 && (
                      <span style={{ color: 'var(--gk-muted)', fontSize: 13 }}>{t('{{count}} coché(s)', { count: selectionCount })}</span>
                    )}
                    <button type="button" className="tx-btn tx-ghost" style={{ height: 38 }} onClick={exportCsv}>
                      <Icon.download /> {t('Exporter CSV')}
                    </button>
                  </div>
                </div>

                <div className="gk-filters">
                  <div className="gk-filters-head">
                    <span className="gk-filters-title">
                      <Icon.filter />
                      {t('Filtres')}
                    </span>
                    <button type="button" className="gk-filters-reset" onClick={resetFilters}>
                      {t('Réinitialiser')}
                    </button>
                  </div>
                  <div className="gk-filters-row">
                    <RangeFilter
                      label={t('Volume de recherche')}
                      hint={t('recherches / mois')}
                      minLabel={t('Min')}
                      maxLabel={t('Max')}
                      minValue={volumeMin}
                      maxValue={volumeMax}
                      onMinChange={setVolumeMin}
                      onMaxChange={setVolumeMax}
                      onApply={applyFilters}
                      step={100}
                    />
                    <RangeFilter
                      label={t('Score d\'intérêt')}
                      hint={t('{{min}} à {{max}}', {
                        min: formatInputNumber(scoreBounds.min),
                        max: formatInputNumber(scoreBounds.max),
                      })}
                      minLabel={t('Min')}
                      maxLabel={t('Max')}
                      minValue={scoreMin}
                      maxValue={scoreMax}
                      onMinChange={setScoreMin}
                      onMaxChange={setScoreMax}
                      onApply={applyFilters}
                      step={1}
                    />
                    <ProductionFilter
                      value={filterDiff}
                      onChange={(diff) => {
                        setFilterDiff(diff);
                        setAppliedFilters((prev) => ({ ...prev, diff }));
                      }}
                    />
                    <button type="button" className="gk-filters-apply" onClick={applyFilters}>
                      {t('Appliquer')}
                    </button>
                  </div>
                  <div className="gk-filters-active">
                    <span>{t('Actifs')} :</span>
                    <span className="gk-filter-chip">
                      {t('Volume')} {formatCompactVolume(parseFilterBound(appliedFilters.volumeMin, volumeBounds.min))} – {formatCompactVolume(parseFilterBound(appliedFilters.volumeMax, volumeBounds.max))}
                      <button type="button" onClick={clearVolumeFilter} aria-label={t('Retirer le filtre de volume')}>
                        ×
                      </button>
                    </span>
                    <span className="gk-filter-chip">
                      {t('Score')} {formatInputNumber(parseFilterBound(appliedFilters.scoreMin, scoreBounds.min))} – {formatInputNumber(parseFilterBound(appliedFilters.scoreMax, scoreBounds.max))}
                      <button type="button" onClick={clearScoreFilter} aria-label={t('Retirer le filtre de score')}>
                        ×
                      </button>
                    </span>
                    {appliedFilters.diff !== 'all' && (
                      <span className="gk-filter-chip">
                        {t('Production')} {appliedFilters.diff}
                        <button type="button" onClick={clearProductionFilter} aria-label={t('Retirer le filtre de production')}>
                          ×
                        </button>
                      </span>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    background: '#fff',
                    border: '1px solid var(--gk-border)',
                    borderRadius: 12,
                    overflow: 'hidden',
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                      <tr style={{ background: '#faf9fc', textAlign: 'left', color: 'var(--gk-ink2)' }}>
                        <th style={{ padding: '13px 16px', width: 40 }}>
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => {
                              const on = !allSelected;
                              const next = { ...selected };
                              visKeys.forEach((k) => {
                                next[k] = on;
                              });
                              setSelected(next);
                            }}
                            style={{ accentColor: 'var(--gk-accent)', cursor: 'pointer', width: 16, height: 16 }}
                          />
                        </th>
                        <th style={{ padding: '13px 8px', fontWeight: 600 }}>{t('Mot-clé')}</th>
                        <th
                          className="sortable"
                          style={{ padding: '13px 8px', width: 210, fontWeight: 600 }}
                          onClick={() => setSort('interest')}
                        >
                          {t('Score d\'intérêt')} {arrow('interest')}
                        </th>
                        <th
                          className="sortable"
                          style={{ padding: '13px 8px', width: 130, fontWeight: 600, textAlign: 'right' }}
                          onClick={() => setSort('volume')}
                        >
                          {t('Volume')} {arrow('volume')}
                        </th>
                        <th style={{ padding: '13px 8px', width: 130, fontWeight: 600 }}>{t('Production')}</th>
                        <th style={{ padding: '13px 16px', width: 120 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedItems.map((r) => (
                        <tr
                          key={r.keyword}
                          className="tx-row"
                          style={{
                            borderTop: '1px solid var(--gk-border)',
                            background: workKw === r.keyword ? 'var(--gk-accent-050)' : 'transparent',
                          }}
                        >
                          <td style={{ padding: '12px 16px' }}>
                            <input
                              type="checkbox"
                              checked={!!selected[r.keyword]}
                              onChange={() =>
                                setSelected((s) => ({ ...s, [r.keyword]: !s[r.keyword] }))
                              }
                              style={{ accentColor: 'var(--gk-accent)', cursor: 'pointer', width: 16, height: 16 }}
                            />
                          </td>
                          <td style={{ padding: '12px 8px', fontWeight: 500 }}>{r.keyword}</td>
                          <td style={{ padding: '12px 8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                              <div
                                style={{
                                  flex: 1,
                                  height: 7,
                                  borderRadius: 4,
                                  background: '#eeedf3',
                                  overflow: 'hidden',
                                }}
                              >
                                <div
                                  style={{
                                    height: '100%',
                                    width: `${r.interest}%`,
                                    borderRadius: 4,
                                    background: 'var(--gk-grad)',
                                  }}
                                />
                              </div>
                              <span
                                style={{
                                  fontSize: 13,
                                  width: 26,
                                  textAlign: 'right',
                                  color: 'var(--gk-accent-600)',
                                  fontWeight: 600,
                                }}
                              >
                                {r.interest}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {fmt(r.volume)}
                          </td>
                          <td style={{ padding: '12px 8px' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '3px 10px',
                                borderRadius: 20,
                                fontSize: 12,
                                fontWeight: 600,
                                ...diffStyle(r.difficulty),
                              }}
                            >
                              {r.difficulty}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                            <button
                              type="button"
                              className="tx-btn"
                              style={{
                                height: 34,
                                padding: '0 14px',
                                fontSize: 13,
                                ...(workKw === r.keyword
                                  ? { background: 'var(--gk-grad)', color: '#fff' }
                                  : {
                                    background: '#fff',
                                    color: 'var(--gk-accent-600)',
                                    borderColor: 'var(--gk-accent-100)',
                                  }),
                              }}
                              onClick={() => handleSelectKeyword(r)}
                            >
                              {workKw === r.keyword ? '✓ ' + t('Choisi') : t('Choisir')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {results.length > 0 && filtered.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 34, color: 'var(--gk-muted)', fontSize: 14 }}>
                      {t('Aucun mot-clé ne correspond aux filtres.')}
                    </div>
                  )}
                  <div className="gk-table-footer">
                    <label className="gk-page-size">
                      <span>{t('Afficher')}</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => setItemsPerPage(+e.target.value)}
                        aria-label={t('Afficher les résultats')}
                      >
                        {PAGE_SIZE_OPTIONS.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                      <span>{t('résultats par page')}</span>
                    </label>
                    <div className="gk-page-range">
                      <button
                        type="button"
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={currentPage <= 1}
                        aria-label={t('Précédent')}
                      >
                        ‹
                      </button>
                      <span>
                        {filtered.length === 0
                          ? `0 ${t('sur')} 0`
                          : `${(currentPage - 1) * itemsPerPage + 1}–${Math.min(currentPage * itemsPerPage, filtered.length)} ${t('sur')} ${filtered.length}`}
                      </span>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((prev) => Math.min(Math.max(1, totalPages), prev + 1))}
                        disabled={currentPage >= Math.max(1, totalPages)}
                        aria-label={t('Suivant')}
                      >
                        ›
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="gk-anim-in">
            <CreateIAbrief
              hideHeader
              initialKeyword={workKw || ''}
              initialContentType={DEFAULT_CONTENT_TYPE}
            />
          </div>
        )}

        {step === 3 && (
          <div className="gk-anim-in">
            <Banner
              icon="✍️"
              title={t('Étape 3 — Rédaction IA')}
              text={t('Générez le contenu final pour « {{keyword}} ».', { keyword: workKw || '' })}
            />
            <div
              style={{
                background: 'var(--gk-card)',
                border: '1px solid var(--gk-border)',
                borderRadius: 14,
                padding: '22px 26px',
                boxShadow: '0 1px 3px rgba(20,18,50,.04)',
                display: 'flex',
                gap: 18,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ color: 'var(--gk-ink2)', fontSize: 14 }}>
                {t('Ouvrez le générateur pour rédiger le contenu à partir du mot-clé retenu.')}
              </span>
              <button
                type="button"
                className="tx-btn tx-primary"
                style={{ marginLeft: 'auto' }}
                onClick={openContentPopin}
                disabled={!workKw}
              >
                <Icon.bolt />  {t('Générer le contenu')}
              </button>
            </div>
          </div>
        )}

      </main>

      {step === 1 && workKw && (
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            background: '#fff',
            borderTop: '1px solid var(--gk-border)',
            boxShadow: '0 -4px 20px -8px rgba(20,18,50,.15)',
          }}
        >
          <div
            style={{
              maxWidth: 1240,
              margin: '0 auto',
              padding: '14px 8px',
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: 'var(--gk-muted)', fontSize: 13 }}>{t('Mot-clé retenu :')}</span>
              <span style={{ fontWeight: 600, fontSize: 15 }}>{workKw}</span>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
              <button type="button" className="tx-btn tx-soft" onClick={goToCreateBrief}>
                <Icon.doc style={{ stroke: 'currentColor' }} /> {t('Générer un brief')}
              </button>
              <button
                type="button"
                className="tx-btn tx-primary"
                onClick={openContentPopin}
              >
                <Icon.bolt /> {t('Générer le contenu')}
              </button>
            </div>
          </div>
        </div>
      )}

      <AssignTextToProjectPopin
        isOpen={isPremiumProjectPopinOpen}
        onClose={() => {
          if (!isLoadingPremiumOrder) {
            setIsPremiumProjectPopinOpen(false);
            setPremiumProjectError('');
          }
        }}
        projects={premiumProjects}
        loadingProjects={loadingPremiumProjects}
        isSaving={isLoadingPremiumOrder}
        errorKey={premiumProjectError}
        onSave={handlePremiumProjectValidate}
        onCreateProject={() => {
          setIsPremiumProjectPopinOpen(false);
          history.push('/AdminClient/CreateProject');
        }}
        keyword={workKw || ''}
        title={t('Choisir un projet Premium')}
        saveButtonLabel={t('Valider le projet')}
      />

      <RedactionModal
        isOpen={isRedactionModalOpen}
        onClose={handleCloseRedactionModal}
        orderId={selectedPremiumOrder?.id}
        orderSiloName={selectedPremiumOrder?.siloName || selectedPremiumOrder?.nomSilo}
        orderWebsite={
          selectedPremiumOrder?.website?.urlSite ||
          selectedWebsite?.urlSite ||
          selectedWebsiteUrl
        }
        language={
          selectedPremiumOrder?.language?.language || selectedPremiumOrder?.language
        }
        country={
          selectedPremiumOrder?.website?.country || selectedWebsite?.country || null
        }
        maxWords={selectedPremiumOrder?.maxWords}
        orderType={selectedPremiumOrder?.type || 'PREMIUM'}
        orderTextType={selectedPremiumOrder?.textType}
        title={t('Rédaction Premium')}
        aiAgentId={selectedPremiumOrder?.aiAgentId}
        orderTextAmount={selectedPremiumOrder?.textAmount}
        initialKeyword={workKw || ''}
        t={t}
        generationContext="AC"
      />

      <SmartContentPopin
        isOpen={isSmartContentPopinOpen}
        onClose={() => setIsSmartContentPopinOpen(false)}
        agents={agents}
        agentsLoading={agentsLoading}
        llmOptions={availableAiModels}
        gapAnalysisEnabled={gapAnalysisEnabled === true}
        showAdvancedSeoSection
        showWritingAxis
        showAgentContentTypeInDropdown
        contentType={DEFAULT_CONTENT_TYPE}
        disableGenerateButton={
          hasNoWebsiteConfigured || (remainingCredits !== null && remainingCredits <= 0)
        }
        onBlockedGenerateClick={() => {
          setIsSmartContentPopinOpen(false);
          if (hasNoWebsiteConfigured) {
            history.push('/AdminClient/AjouterSiteWeb');
            return;
          }
          setIsCreditsLimitModalOpen(true);
        }}
        initialValues={smartPopinInitialValues}
        language={selectedWebsite?.language || null}
        country={selectedWebsite?.country || null}
        onSubmit={handleSmartContentPopinSubmit}
        mode="create"
      />

      <ProjectCreatedModal
        isOpen={isCreditsLimitModalOpen}
        onClose={() => setIsCreditsLimitModalOpen(false)}
        onConfirm={() => {
          setIsCreditsLimitModalOpen(false);
          setIsBuyCreditsModalOpen(true);
        }}
        title={t('Limite atteinte')}
        message={t(
          'Vous avez atteint la limite autorisée par votre forfait.\n\nRechargez votre compte dès maintenant pour continuer à générer du contenu, enrichir vos textes et utiliser vos agents sans interruption.'
        )}
        confirmButtonText={t('Acheter des crédits')}
        secondaryButtonText={t('Passer à une offre supérieure')}
        onSecondaryAction={() => history.push('/AdminClient/Forfait')}
      />
      <BuyCreditsModal
        open={isBuyCreditsModalOpen}
        onClose={() => setIsBuyCreditsModalOpen(false)}
      />
    </div>
  );
};

export default GenerateKeyword;
